/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MapPinIcon, TrashIcon } from "@heroicons/react/24/outline";
import { createBranch, updateBranch, deleteBranch } from "../../api/mutations";
import { useLanguage } from "../../contexts/LanguageContext";
import { usePlanAlert } from "../../contexts/PlanAlertContext";
import { useNotification } from "../../contexts/NotificationContext";
import { getApiErrorMessages } from "../../utils/apiErrorReasons";
import { Modal, ErrorMessages } from "../../components/ui/Modal";
import { buttonClass, cardClass } from "../../components/ui/styles";
import BranchForm from "./BranchForm";
import { hasRunningSubscription } from "./storePayload";

const TOAST_MS = 4000;

// Adding a branch needs a running subscription or an unexpired trial, as before.
function canAddBranches(store, now = new Date()) {
  if (hasRunningSubscription(store)) return true;
  if (!store?.trial_expires_at) return false;
  return now < new Date(store.trial_expires_at);
}

export default function BranchesSection({ store, branches }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { showPlanAlert } = usePlanAlert();
  const notifications = useNotification();
  // "new" for the add form, a branch id while editing, null otherwise.
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [blocked, setBlocked] = useState(false);

  const limit = store.features?.branch_limit;
  const toast = (title) =>
    notifications?.showNotification(title, "", "success", [], TOAST_MS);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["stores"] });
    queryClient.invalidateQueries({ queryKey: ["branches", store.id] });
  };

  const createMutation = useMutation({
    mutationFn: createBranch,
    onSuccess: () => {
      refresh();
      setEditing(null);
      toast(t("settings.branches.toast.added"));
    },
  });
  const updateMutation = useMutation({
    mutationFn: updateBranch,
    onSuccess: () => {
      refresh();
      setEditing(null);
      toast(t("settings.branches.toast.saved"));
    },
  });
  const deleteMutation = useMutation({
    mutationFn: deleteBranch,
    onSuccess: () => {
      refresh();
      setDeleting(null);
    },
  });

  const startAdd = () => {
    // Fails open on purpose when the limit is missing: the API does not enforce
    // it either, and an older payload must not lock merchants out.
    if (limit <= branches.length) {
      showPlanAlert({
        title: t("featureLimit.title"),
        description: t("featureLimit.description", {
          plan: store.current_plan,
          limit,
          feature: t("featureLimit.features.branch", { count: limit }),
        }),
      });
      return;
    }
    if (!canAddBranches(store)) {
      setBlocked(true);
      return;
    }
    createMutation.reset();
    setEditing("new");
  };

  const startEdit = (branch) => {
    updateMutation.reset();
    setEditing(branch.id);
  };

  const formErrors = (mutation) =>
    mutation.isError
      ? getApiErrorMessages(mutation.error, t, "settings.branches.errors.save")
      : null;

  return (
    <section
      aria-labelledby="settings-branches-title"
      className={`${cardClass} flex flex-col gap-4 p-6`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2
            id="settings-branches-title"
            className="text-[17px] font-semibold text-gray-900"
          >
            {t("settings.branches.title")}
          </h2>
          {Number.isFinite(limit) && (
            <span className="text-[13px] text-gray-600">
              {t("settings.branches.usage", {
                used: branches.length,
                // the plural is on the limit ("of 4 branches"); `count` names it
                count: limit,
              })}
            </span>
          )}
        </div>
        {editing !== "new" && (
          <button
            type="button"
            onClick={startAdd}
            className={buttonClass.secondary}
          >
            {t("settings.branches.addButton")}
          </button>
        )}
      </div>

      {blocked && (
        <p
          role="alert"
          className="flex flex-wrap items-center gap-2 rounded-[10px] bg-orange-50 px-3 py-2.5 text-sm text-orange-900"
        >
          {t("settings.branches.trialEnded")}
          <Link
            to="/pricing"
            className="font-semibold text-indigo-700 hover:text-indigo-800"
          >
            {t("settings.plan.viewPlans")}
          </Link>
        </p>
      )}

      <div className="flex flex-col gap-2.5">
        {branches.map((branch) =>
          editing === branch.id ? (
            <BranchForm
              key={branch.id}
              branch={branch}
              isPending={updateMutation.isPending}
              errors={formErrors(updateMutation)}
              onCancel={() => setEditing(null)}
              onSubmit={(values) =>
                updateMutation.mutate({
                  storeId: store.id,
                  branchId: branch.id,
                  ...values,
                })
              }
            />
          ) : (
            <div
              key={branch.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3.5"
            >
              <span className="flex min-w-0 items-start gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-indigo-50 text-indigo-800"
                >
                  <MapPinIcon className="h-4 w-4" />
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-semibold text-gray-900">
                    {branch.name}
                  </span>
                  <span className="text-[13px] break-words text-gray-600">
                    {branch.address}
                  </span>
                </span>
              </span>
              <span className="flex shrink-0 gap-1.5">
                <button
                  type="button"
                  onClick={() => startEdit(branch)}
                  aria-label={t("settings.branches.editLabel", {
                    name: branch.name,
                  })}
                  className={buttonClass.small}
                >
                  {t("settings.branches.edit")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    deleteMutation.reset();
                    setDeleting(branch);
                  }}
                  aria-label={t("settings.branches.deleteLabel", {
                    name: branch.name,
                  })}
                  title={t("settings.branches.deleteLabel", {
                    name: branch.name,
                  })}
                  className={buttonClass.dangerIcon}
                >
                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </span>
            </div>
          ),
        )}

        {branches.length === 0 && editing !== "new" && (
          <div className="flex flex-col items-start gap-2 rounded-xl border-[1.5px] border-dashed border-gray-300 px-4 py-[18px]">
            <span className="text-sm font-semibold text-gray-900">
              {t("settings.branches.empty.title")}
            </span>
            <span className="text-[13px] leading-normal text-gray-600">
              {t("settings.branches.empty.body")}
            </span>
          </div>
        )}

        {editing === "new" && (
          <BranchForm
            branch={null}
            isPending={createMutation.isPending}
            errors={formErrors(createMutation)}
            onCancel={() => setEditing(null)}
            onSubmit={(values) =>
              createMutation.mutate({ storeId: store.id, ...values })
            }
          />
        )}
      </div>

      <Modal
        open={!!deleting}
        onClose={() => !deleteMutation.isPending && setDeleting(null)}
        role="alertdialog"
        title={t("settings.branches.delete.title", {
          name: deleting?.name || "",
        })}
        footer={
          <>
            <button
              type="button"
              onClick={() => setDeleting(null)}
              disabled={deleteMutation.isPending}
              className={buttonClass.secondary}
              data-autofocus
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleting &&
                deleteMutation.mutate(
                  { storeId: store.id, branchId: deleting.id },
                  {
                    onSuccess: () =>
                      toast(
                        t("settings.branches.toast.deleted", {
                          name: deleting.name,
                        }),
                      ),
                  },
                )
              }
              className={buttonClass.danger}
            >
              {t("settings.branches.delete.confirm")}
            </button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-gray-700">
          {t("settings.branches.delete.body")}
        </p>
        {deleteMutation.isError && (
          <ErrorMessages
            messages={getApiErrorMessages(
              deleteMutation.error,
              t,
              "settings.branches.errors.delete",
            )}
          />
        )}
      </Modal>
    </section>
  );
}
